// "Ajustes e regras já criados" da tela de Desembolso sob a política Ampli: as regras de
// pagamento, os grupos de insumos e os ajustes de mês que a obra já tem, no bloco que a
// moldura (`DisbursementWorkspace`) põe ACIMA da EAP.
//
// É um bloco de LEITURA e de atalho para o editor: cada linha abre o editor que já
// existe (regra, grupo) ou remove o ajuste. Nada aqui calcula desembolso — os números
// (cobertura, valor aplicado, excedente) vêm do `GET .../disbursement/rules`.
//
// Fica visível com a EAP carregando ou vazia: depende só da lista de regras, que é a outra
// leitura da tela. Sem nada criado, declara a ausência no lugar onde a lista apareceria.
//
// O cabeçalho do bloco é o lugar das ações de CRIAÇÃO (organizar com IA, nova regra, novo
// grupo): ficam junto do que produzem, e o resumo do topo continua só informando.
import { useState } from "react";

import { quotaNote } from "./ai/useAiQuota";

import { describeProfile } from "./profile";
import { competenceText, money } from "./format";

/** Uma regra com forma ilegível não derruba o bloco: a linha declara que a forma não está disponível. */
const profileText = (profile) => {
  try {
    return describeProfile(profile);
  } catch {
    return "Forma indisponível";
  }
};

/**
 * `money()` lê o contexto do projeto por dentro (um hook disfarçado), então cada linha é
 * um componente: a contagem de hooks do bloco não pode depender do tamanho das listas.
 */
const RuleRow = ({ rule, readOnly, onEditRule }) => (
  <li className="ampli-created__row" data-kind="rule">
    <div className="ampli-created__main">
      <strong>{rule.target?.label ?? rule.target?.key}</strong>
      <span className="ampli-created__detail">{profileText(rule.profile)}</span>
      {rule.active === false ? <span className="badge">inativa</span> : null}
    </div>
    <span className="ampli-created__value">{money(rule.coveredCents)}</span>
    {!readOnly ? (
      <button
        type="button"
        className="button button-outline small"
        onClick={() => onEditRule?.(rule)}
      >
        Editar regra
      </button>
    ) : null}
  </li>
);

const GroupRow = ({ group, readOnly, onEditGroup }) => (
  <li className="ampli-created__row" data-kind="group">
    <div className="ampli-created__main">
      <strong>{group.name}</strong>
      <span className="ampli-created__detail">
        {group.memberCount == null
          ? "Insumos indisponíveis"
          : `${group.memberCount.toLocaleString("pt-BR")} insumos`}
      </span>
    </div>
    <span className="ampli-created__value">{money(group.coveredCents)}</span>
    {!readOnly ? (
      <button
        type="button"
        className="button button-outline small"
        onClick={() => onEditGroup?.(group)}
      >
        Editar grupo
      </button>
    ) : null}
  </li>
);

const AdjustmentRow = ({ adjustment, readOnly, removing, onRemove }) => (
  <li className="ampli-created__row" data-kind="adjustment">
    <div className="ampli-created__main">
      <strong>{adjustment.label ?? adjustment.wbsCodeId}</strong>
      <span className="ampli-created__detail">
        {`Mover de ${competenceText(adjustment.fromCompetence)} para ${competenceText(adjustment.toCompetence)}`}
      </span>
      {adjustment.exceedsCents > 0 ? (
        <span className="warning">
          {`${money(adjustment.exceedsCents)} não couberam no mês de origem.`}
        </span>
      ) : null}
    </div>
    <span className="ampli-created__value">{money(adjustment.amountCents)}</span>
    {!readOnly ? (
      <button
        type="button"
        className="button button-error small"
        disabled={removing}
        onClick={() => onRemove?.(adjustment)}
      >
        {removing ? "Removendo…" : "Remover"}
      </button>
    ) : null}
  </li>
);

const Section = ({ title, children }) => (
  <section className="ampli-created__section">
    <h4 className="ampli-created__section-title">{title}</h4>
    <ul>{children}</ul>
  </section>
);

const AmpliCreatedBlock = ({
  items = [],
  loaded = true,
  readOnly = false,
  onEditRule,
  onEditGroup,
  onRemoveAdjustment,
  onNewRule,
  onNewGroup,
  onOrganizeWithAi,
  aiQuota = null,
}) => {
  const [removing, setRemoving] = useState(null);
  const rules = items.filter((item) => item.kind === "rule");
  const groups = items.filter((item) => item.kind === "group");
  const adjustments = items.filter(
    (item) => item.kind === "adjustment" && item.active !== false,
  );

  const remove = async (adjustment) => {
    if (removing) return;
    setRemoving(adjustment.entryId);
    try {
      await onRemoveAdjustment?.(adjustment);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="ampli-created">
      <header className="ampli-created__header">
        <div className="ampli-created__heading">
          <h3 className="ampli-created__title">Ajustes e regras já criados</h3>
          <p className="ampli-created__subtitle">
            Regras, grupos e ajustes que mudam a curva da obra.
          </p>
        </div>
        {!readOnly ? (
          <div className="ampli-created__toolbar">
            <div className="ampli-created__ai-action">
              <button
                type="button"
                className="button button-accent small"
                data-testid="disbursement-organize-ai"
                onClick={onOrganizeWithAi}
              >
                Organizar com IA
              </button>
              {quotaNote(aiQuota) ? (
                <span
                  className="ampli-created__ai-quota"
                  data-testid="disbursement-ai-quota"
                  title="A cota de IA é compartilhada com as outras telas da obra."
                >
                  {quotaNote(aiQuota)}
                </span>
              ) : null}
            </div>
            <button
              type="button"
              className="button button-outline small"
              onClick={onNewRule}
            >
              Nova regra
            </button>
            <button
              type="button"
              className="button button-outline small"
              onClick={onNewGroup}
            >
              Novo grupo
            </button>
          </div>
        ) : null}
      </header>
      {!loaded ? <p className="card-label">Carregando…</p> : null}
      {loaded && rules.length + groups.length + adjustments.length === 0 ? (
        <p className="empty-state">
          Nenhuma regra, grupo ou ajuste foi criado nesta obra ainda.
        </p>
      ) : null}
      {rules.length > 0 ? (
        <Section title="Regras de pagamento">
          {rules.map((rule) => (
            <RuleRow
              key={rule.rowId}
              rule={rule}
              readOnly={readOnly}
              onEditRule={onEditRule}
            />
          ))}
        </Section>
      ) : null}
      {groups.length > 0 ? (
        <Section title="Grupos de insumos">
          {groups.map((group) => (
            <GroupRow
              key={group.rowId}
              group={group}
              readOnly={readOnly}
              onEditGroup={onEditGroup}
            />
          ))}
        </Section>
      ) : null}
      {adjustments.length > 0 ? (
        <Section title="Ajustes de mês">
          {adjustments.map((adjustment) => (
            <AdjustmentRow
              key={adjustment.rowId}
              adjustment={adjustment}
              readOnly={readOnly}
              removing={removing === adjustment.entryId}
              onRemove={remove}
            />
          ))}
        </Section>
      ) : null}
    </div>
  );
};

export default AmpliCreatedBlock;
